import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import axios from "axios";
import { Users, Stethoscope, Hospital, Plus, Edit2, X, Check, Trash2, UserPlus, ScrollText, ChevronUp, ChevronDown, ClipboardList } from "lucide-react";
import Modal, { DialogHeader, DialogFooter, DialogBody, ModalCloseButton } from '../components/ui/Modal';
import PillTabs from '../components/ui/PillTabs';
import { PageEnter } from '../motion';
import { AnimatedNumber } from '../motion';
import { parseBackendDate } from '../utils/datetime';
import ProcessingActivitiesPanel from '../components/compliance/ProcessingActivitiesPanel';
import BrandLogo from '../components/BrandLogo';

const formatAuditDateTime = (value) => {
  const date = parseBackendDate(value);
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey) {
    return (
      <span className="inline-flex flex-col ms-1 opacity-30 align-middle">
        <ChevronUp className="w-3 h-3 -mb-1" />
        <ChevronDown className="w-3 h-3" />
      </span>
    );
  }
  return sortConfig.direction === "asc" ? (
    <ChevronUp className="w-4 h-4 inline ms-1 align-middle" />
  ) : (
    <ChevronDown className="w-4 h-4 inline ms-1 align-middle" />
  );
};

const GENDER_FILTER_OPTIONS = [["", "All"], ["Male", "Male"], ["Female", "Female"]];
const STATUS_FILTER_OPTIONS = [["", "All"], ["Active", "Active"], ["Not active", "Not active"]];
const YES_NO_FILTER_OPTIONS = [["", "All"], ["Yes", "Yes"], ["No", "No"]];
const OUTCOME_FILTER_OPTIONS = [["", "All"], ["success", "Success"], ["denied", "Denied"], ["failure", "Failure"]];
const HTTP_METHOD_FILTER_OPTIONS = [["", "All"], ["GET", "GET"], ["POST", "POST"], ["PUT", "PUT"], ["PATCH", "PATCH"], ["DELETE", "DELETE"]];

const sortRows = (rows, sortConfig, valueGetter) => {
  const sorted = [...rows];
  const dir = sortConfig.direction === "asc" ? 1 : -1;
  sorted.sort((a, b) => {
    const av = valueGetter(a, sortConfig.key);
    const bv = valueGetter(b, sortConfig.key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
  });
  return sorted;
};

const HeaderFilterSelect = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onClick={(e) => e.stopPropagation()}
    className="ms-1 text-xs border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-600 max-w-[88px]"
  >
    {options.map(([val, label]) => (
      <option key={val || "all"} value={val}>{label}</option>
    ))}
  </select>
);

const SortableHeader = ({
  label,
  columnKey,
  sortConfig,
  onSort,
  filterValue,
  onFilterChange,
  filterOptions,
  className = "px-4 py-3 text-start text-sm font-semibold text-gray-700",
}) => (
  <th
    className={`${className} cursor-pointer select-none whitespace-nowrap`}
    onClick={() => onSort(columnKey)}
  >
    <span className="inline-flex items-center">
      {label}
      <SortIcon columnKey={columnKey} sortConfig={sortConfig} />
      {filterOptions && onFilterChange ? (
        <HeaderFilterSelect value={filterValue} onChange={onFilterChange} options={filterOptions} />
      ) : null}
    </span>
  </th>
);

const AdminPanel = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const [clinicians, setClinicians] = useState([]);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [filteredNurses, setFilteredNurses] = useState([]);
  const [patientSort, setPatientSort] = useState({ key: "name", direction: "asc" });
  const [doctorSort, setDoctorSort] = useState({ key: "name", direction: "asc" });
  const [nurseSort, setNurseSort] = useState({ key: "name", direction: "asc" });
  const [patientGenderFilter, setPatientGenderFilter] = useState("");
  const [patientStatusFilter, setPatientStatusFilter] = useState("");
  const [doctorGenderFilter, setDoctorGenderFilter] = useState("");
  const [doctorStatusFilter, setDoctorStatusFilter] = useState("");
  const [doctorMedsFilter, setDoctorMedsFilter] = useState("");
  const [nurseGenderFilter, setNurseGenderFilter] = useState("");
  const [nurseStatusFilter, setNurseStatusFilter] = useState("");
  const [nurseMedsFilter, setNurseMedsFilter] = useState("");
  const [activeTab, setActiveTab] = useState("patients");
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const auditPageSize = 100;
  const [auditSort, setAuditSort] = useState({ key: "occurred_at", direction: "desc" });
  const [auditOutcomeFilter, setAuditOutcomeFilter] = useState("");
  const [auditMethodFilter, setAuditMethodFilter] = useState("");
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionTotal, setDeletionTotal] = useState(0);
  const [deletionSkip, setDeletionSkip] = useState(0);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const deletionPageSize = 50;
  const [deletionSort, setDeletionSort] = useState({ key: "requested_at", direction: "desc" });
  const [deletionStatusFilter, setDeletionStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showDoctorAssignModal, setShowDoctorAssignModal] = useState(false);
  const [selectedPatientForDoctors, setSelectedPatientForDoctors] = useState(null);
  const [selectedDoctorsToAssign, setSelectedDoctorsToAssign] = useState([]);
  const [showNurseAssignModal, setShowNurseAssignModal] = useState(false);
  const [selectedPatientForNurses, setSelectedPatientForNurses] = useState(null);
  const [selectedNursesToAssign, setSelectedNursesToAssign] = useState([]);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    role: "",
    canAssignMedications: "No",
  });

  useEffect(() => {
    if (activeTab !== "audit") return;
    let cancelled = false;
    (async () => {
      setAuditLoading(true);
      try {
        const token = getToken();
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const params = new URLSearchParams({
          skip: String(auditSkip),
          limit: String(auditPageSize),
          sort_by: auditSort.key,
          sort_dir: auditSort.direction,
        });
        if (auditOutcomeFilter) params.set("outcome", auditOutcomeFilter);
        if (auditMethodFilter) params.set("http_method", auditMethodFilter);
        const res = await axios.get(
          `${API_BASE_URL}/compliance/audit-logs?${params.toString()}`,
          { headers }
        );
        if (cancelled) return;
        setAuditLogs(res.data?.items || []);
        setAuditTotal(res.data?.total ?? 0);
      } catch (e) {
        if (!cancelled) {
          console.error("Audit log fetch failed", e);
          setAuditLogs([]);
          setAuditTotal(0);
        }
      } finally {
        if (!cancelled) setAuditLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, auditSkip, auditPageSize, auditSort, auditOutcomeFilter, auditMethodFilter, API_BASE_URL]);

  useEffect(() => {
    if (activeTab !== "deletions") return;
    let cancelled = false;
    (async () => {
      setDeletionLoading(true);
      try {
        const token = getToken();
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const params = new URLSearchParams({
          skip: String(deletionSkip),
          limit: String(deletionPageSize),
          sort_by: deletionSort.key,
          sort_dir: deletionSort.direction,
        });
        if (deletionStatusFilter) params.set("status", deletionStatusFilter);
        const res = await axios.get(
          `${API_BASE_URL}/compliance/deletion-requests?${params.toString()}`,
          { headers }
        );
        if (cancelled) return;
        setDeletionRequests(res.data?.items || []);
        setDeletionTotal(res.data?.total ?? 0);
      } catch (e) {
        if (!cancelled) {
          console.error("Deletion requests fetch failed", e);
          setDeletionRequests([]);
          setDeletionTotal(0);
        }
      } finally {
        if (!cancelled) setDeletionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, deletionSkip, deletionPageSize, deletionSort, deletionStatusFilter, API_BASE_URL]);

  const fetchData = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const [cliniciansRes, patientsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/clinicians/`, { headers }),
        axios.get(`${API_BASE_URL}/patients/`, { headers }),
      ]);

      const allClinicians = Array.isArray(cliniciansRes.data)
        ? cliniciansRes.data
        : cliniciansRes.data?.data || [];

      const allPatients = Array.isArray(patientsRes.data)
        ? patientsRes.data
        : patientsRes.data?.data || [];

      setClinicians(allClinicians);
      setPatients(allPatients.map((p) => ({
        ...p,
        status: p.status || (p.isActive === false ? "Not active" : "Active"),
      })));
      setFilteredPatients(allPatients.map((p) => ({
        ...p,
        status: p.status || (p.isActive === false ? "Not active" : "Active"),
      })));

      const doctors = allClinicians
        .filter(c => c.role?.toLowerCase() === "doctor")
        .map((c) => ({
          ...c,
          status: c.status || (c.isActive === false || c.active === false ? "Not active" : "Active"),
          canAssignMedications: c.canAssignMedications || "No",
        }));
      const nurses = allClinicians
        .filter(c => c.role?.toLowerCase() === "nurse")
        .map((c) => ({
          ...c,
          status: c.status || (c.isActive === false || c.active === false ? "Not active" : "Active"),
          canAssignMedications: c.canAssignMedications || "No",
        }));

      setFilteredDoctors(doctors);
      setFilteredNurses(nurses);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const doctors = clinicians
    .filter(c => c.role?.toLowerCase() === "doctor")
    .map((c) => ({
      ...c,
      status: c.status || (c.isActive === false || c.active === false ? "Not active" : "Active"),
      canAssignMedications: c.canAssignMedications || "No",
    }));
  const nurses = clinicians
    .filter(c => c.role?.toLowerCase() === "nurse")
    .map((c) => ({
      ...c,
      status: c.status || (c.isActive === false || c.active === false ? "Not active" : "Active"),
      canAssignMedications: c.canAssignMedications || "No",
    }));

  const handleSort = (table, columnKey) => {
    const sortMap = {
      audit: [auditSort, setAuditSort, setAuditSkip],
      deletions: [deletionSort, setDeletionSort, setDeletionSkip],
      patients: [patientSort, setPatientSort, null],
      doctors: [doctorSort, setDoctorSort, null],
      nurses: [nurseSort, setNurseSort, null],
    };
    const [current, setter, skipSetter] = sortMap[table];
    setter({
      key: columnKey,
      direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc",
    });
    if (skipSetter) skipSetter(0);
  };

  const handleExportAudit = async (format) => {
    try {
      const token = getToken();
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };

      const params = new URLSearchParams({
        format: format,
        skip: String(auditSkip),
        limit: String(auditPageSize),
        sort_by: auditSort.key,
        sort_dir: auditSort.direction,
      });
      if (auditOutcomeFilter) params.set("outcome", auditOutcomeFilter);
      if (auditMethodFilter) params.set("http_method", auditMethodFilter);

      const res = await axios.get(
        `${API_BASE_URL}/compliance/audit-logs/export?${params.toString()}`,
        { headers, responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const ext = String(format).toLowerCase().includes("pdf") ? "pdf" : "xls";

      const link = document.createElement("a");
      link.href = url;
      link.download = `audit_logs.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Audit export failed:", error);
      alert("Failed to export audit logs.");
    }
  };

  const handleToggleActiveStatus = async (type, item) => {
    const isCurrentlyActive = item.status !== "Not active" && item.isActive !== false && item.active !== false;
    const nextActive = !isCurrentlyActive;
    const actionLabel = nextActive ? "activate" : "deactivate";
    const name = `${item.firstName || ""} ${item.lastName || ""}`.trim();
    if (!window.confirm(`Are you sure you want to ${actionLabel} ${name || "this user"}?`)) {
      return;
    }

    try {
      const endpoint =
        type === "patient"
          ? `${API_BASE_URL}/patients/${item.id}/active-status`
          : `${API_BASE_URL}/clinicians/${item.id}/active-status`;
      await axios.patch(endpoint, { is_active: nextActive }, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      console.error("Error updating active status:", error);
      alert(`Failed to ${actionLabel} user. Please check the console for details.`);
    }
  };

  const accountStatusClass = (status) =>
    status === "Not active"
      ? "bg-gray-100 text-gray-700"
      : "bg-green-100 text-green-700";

  const patientValueGetter = (row, key) => {
    if (key === "name") return `${row.firstName || ""} ${row.lastName || ""}`.trim();
    if (key === "gender") return row.gender || "";
    if (key === "clinician") {
      return (row.doctors || [])
        .map((d) => `${d.firstName || ""} ${d.lastName || ""}`.trim())
        .join(", ");
    }
    if (key === "status") return row.status || "Active";
    return row[key];
  };

  const clinicianValueGetter = (row, key) => {
    if (key === "name") return `${row.firstName || ""} ${row.lastName || ""}`.trim();
    if (key === "gender") return row.gender || "";
    if (key === "canAssignMedications") return row.canAssignMedications || "No";
    if (key === "status") return row.status || "Active";
    return row[key];
  };

  const visiblePatients = useMemo(() => {
    let rows = filteredPatients.filter((p) => {
      if (patientGenderFilter && (p.gender || "") !== patientGenderFilter) return false;
      if (patientStatusFilter && (p.status || "Active") !== patientStatusFilter) return false;
      return true;
    });
    return sortRows(rows, patientSort, patientValueGetter);
  }, [filteredPatients, patientSort, patientGenderFilter, patientStatusFilter]);

  const visibleDoctors = useMemo(() => {
    let rows = filteredDoctors.filter((d) => {
      if (doctorGenderFilter && (d.gender || "") !== doctorGenderFilter) return false;
      if (doctorStatusFilter && (d.status || "Active") !== doctorStatusFilter) return false;
      if (doctorMedsFilter && (d.canAssignMedications || "No") !== doctorMedsFilter) return false;
      return true;
    });
    return sortRows(rows, doctorSort, clinicianValueGetter);
  }, [filteredDoctors, doctorSort, doctorGenderFilter, doctorStatusFilter, doctorMedsFilter]);

  const visibleNurses = useMemo(() => {
    let rows = filteredNurses.filter((n) => {
      if (nurseGenderFilter && (n.gender || "") !== nurseGenderFilter) return false;
      if (nurseStatusFilter && (n.status || "Active") !== nurseStatusFilter) return false;
      if (nurseMedsFilter && (n.canAssignMedications || "No") !== nurseMedsFilter) return false;
      return true;
    });
    return sortRows(rows, nurseSort, clinicianValueGetter);
  }, [filteredNurses, nurseSort, nurseGenderFilter, nurseStatusFilter, nurseMedsFilter]);

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);

    if (item) {
      setFormData({
        firstName: item.firstName || "",
        lastName: item.lastName || "",
        gender: item.gender || "",
        role: item.role || "",
        canAssignMedications: item.canAssignMedications || "No",
      });
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        gender: "",
        dob: "",
        email: "",
        username: "",
        role: type === "doctor" ? "Doctor" : type === "nurse" ? "Nurse" : "",
        canAssignMedications: "No",
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      firstName: "",
      lastName: "",
      gender: "",
      dob: "",
      email: "",
      username: "",
      role: "",
      canAssignMedications: "No",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let payload = {};

      if (modalType === "patient") {
        payload = {
          firstName: formData.firstName,
          middleName: formData.middletName,
          lastName: formData.lastName,
          gender: formData.gender || null,
          dob: formData.dob,
          email: formData.email,
          username: formData.username
        };

        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === "") {
            delete payload[key];
          }
        });

        if (editingItem) {
          await axios.put(`${API_BASE_URL}/patients/${editingItem.id}`, payload, { headers: getAuthHeaders() });
        } else {
          await axios.post(`${API_BASE_URL}/patients/`, payload, { headers: getAuthHeaders() });
        }
      } else {
        payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender || null,
          dob: formData.dob,
          email: formData.email,
          username: formData.username,
          role: modalType === "doctor" ? "Doctor" : "Nurse",
          canAssignMedications: formData.canAssignMedications,
          department_id: 1
        };

        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === "") {
            delete payload[key];
          }
        });

        if (editingItem) {
          await axios.put(`${API_BASE_URL}/clinicians/${editingItem.id}`, payload, { headers: getAuthHeaders() });
        } else {
          await axios.post(`${API_BASE_URL}/clinicians/`, payload, { headers: getAuthHeaders() });
        }
      }

      closeModal();
      fetchData();

    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save. Please check the console for details.");
    }
  };

  const handleMedicationToggle = async (id, currentValue) => {
    try {
      const newValue = currentValue === "Yes" ? "No" : "Yes";
      await axios.patch(
        `${API_BASE_URL}/clinicians/${id}/medication-assignment`,
        { canAssignMedications: newValue },
        { headers: getAuthHeaders() }
      );
      fetchData();
    } catch (error) {
      console.error("Error updating medication permission:", error);
      alert("Failed to update medication assignment permission.");
    }
  };

  const openDeleteConfirm = (type, item) => {
    setDeleteItem({ type, item });
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      const { type, item } = deleteItem;
      
      if (type === "patient") {
        await axios.delete(`${API_BASE_URL}/patients/${item.id}`, { headers: getAuthHeaders() });
      } else {
        await axios.delete(`${API_BASE_URL}/clinicians/${item.id}`, { headers: getAuthHeaders() });
      }

      closeDeleteConfirm();
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete. Please check the console for details.");
    }
  };

  const openDoctorAssignModal = (patient) => {
    setSelectedPatientForDoctors(patient);
    setSelectedDoctorsToAssign(patient.doctors?.map(d => d.id) || []);
    setShowDoctorAssignModal(true);
  };

  const closeDoctorAssignModal = () => {
    setShowDoctorAssignModal(false);
    setSelectedPatientForDoctors(null);
    setSelectedDoctorsToAssign([]);
  };

  const toggleDoctorSelection = (doctorId) => {
    setSelectedDoctorsToAssign(prev => {
      if (prev.includes(doctorId)) {
        return prev.filter(id => id !== doctorId);
      } else {
        return [...prev, doctorId];
      }
    });
  };

  const handleDoctorAssignment = async () => {
    if (!selectedPatientForDoctors) return;

    try {
      const currentDoctorIds = selectedPatientForDoctors.doctors?.map(d => d.id) || [];
      
      const doctorsToAdd = selectedDoctorsToAssign.filter(id => !currentDoctorIds.includes(id));
      
      const doctorsToRemove = currentDoctorIds.filter(id => !selectedDoctorsToAssign.includes(id));

      const patientMRN = selectedPatientForDoctors.MRN;

      for (const doctorId of doctorsToAdd) {
        await axios.post(
          `${API_BASE_URL}/patients/mrn/${patientMRN}/doctors/${doctorId}`,
          {},
          { headers: getAuthHeaders() }
        );
      }

      for (const doctorId of doctorsToRemove) {
        await axios.delete(
          `${API_BASE_URL}/patients/mrn/${patientMRN}/doctors/${doctorId}`,
          {},
          { headers: getAuthHeaders() }
        );
      }

      closeDoctorAssignModal();
      fetchData();
    } catch (error) {
      console.error("Error updating doctor assignments:", error);
      alert("Failed to update doctor assignments. Please check the console for details.");
    }
  };

  const openNurseAssignModal = async (patient) => {
    setSelectedPatientForNurses(patient);
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/patients/mrn/${patient.MRN}/nurses`,
        { headers: getAuthHeaders() }
      );
      const currentNurses = Array.isArray(response.data) ? response.data : [];
      setSelectedNursesToAssign(currentNurses.map(n => n.id));
    } catch (error) {
      console.error("Error fetching current nurses:", error);
      setSelectedNursesToAssign([]);
    }
    
    setShowNurseAssignModal(true);
  };

  const closeNurseAssignModal = () => {
    setShowNurseAssignModal(false);
    setSelectedPatientForNurses(null);
    setSelectedNursesToAssign([]);
  };

  const toggleNurseSelection = (nurseId) => {
    setSelectedNursesToAssign(prev => {
      if (prev.includes(nurseId)) {
        return prev.filter(id => id !== nurseId);
      } else {
        return [...prev, nurseId];
      }
    });
  };

  const handleNurseAssignment = async () => {
    if (!selectedPatientForNurses) return;

    try {
      const currentNursesResponse = await axios.get(
        `${API_BASE_URL}/patients/mrn/${selectedPatientForNurses.MRN}/nurses`,
        { headers: getAuthHeaders() }
      );
      const currentNurses = Array.isArray(currentNursesResponse.data) ? currentNursesResponse.data : [];
      const currentNurseIds = currentNurses.map(n => n.id);
      
      const nursesToAdd = selectedNursesToAssign.filter(id => !currentNurseIds.includes(id));
      
      const nursesToRemove = currentNurseIds.filter(id => !selectedNursesToAssign.includes(id));

      const patientMRN = selectedPatientForNurses.MRN;

      for (const nurseId of nursesToAdd) {
        await axios.post(
          `${API_BASE_URL}/patients/mrn/${patientMRN}/nurses/${nurseId}`,
          {},
          { headers: getAuthHeaders() }
        );
      }

      for (const nurseId of nursesToRemove) {
        await axios.delete(
          `${API_BASE_URL}/patients/mrn/${patientMRN}/nurses/${nurseId}`,
          { headers: getAuthHeaders() }
        );
      }

      closeNurseAssignModal();
      fetchData();
    } catch (error) {
      console.error("Error updating nurse assignments:", error);
      alert("Failed to update nurse assignments. Please check the console for details.");
    }
  };

  const adminTabOptions = [
    { id: "patients", label: "Patients", icon: Users, count: patients.length },
    { id: "doctors", label: "Doctors", icon: Stethoscope, count: doctors.length },
    { id: "nurses", label: "Nurses", icon: Hospital, count: nurses.length },
    {
      id: "audit",
      label: "Audit Log",
      icon: ScrollText,
      count: auditTotal > 999 ? "999+" : auditTotal,
      onBeforeSelect: () => setAuditSkip(0),
    },
    {
      id: "deletions",
      label: "Delete Requests",
      icon: Trash2,
      count: deletionTotal > 999 ? "999+" : deletionTotal,
      onBeforeSelect: () => setDeletionSkip(0),
    },
    {
      id: "processing-activities",
      label: "Processing Activities",
      icon: ClipboardList,
      count: "—",
    },
  ];

  return (
    <div className="min-h-screen vitra-atmosphere">
      
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <BrandLogo to="/administrator" asLink markClassName="h-10 w-10" wordmarkClassName="h-6" />
              <p className="text-sm text-gray-500 hidden sm:block border-l border-gray-200 pl-3">Administrator Panel</p>
            </div>
            <div className="flex gap-3">
              <button className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium">
                {"Admin Control"}
              </button>
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-sm"
              >
                {"Sign Out"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PageEnter className="max-w-7xl mx-auto px-6 py-8">
        
        <div className="grid grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => setActiveTab("patients")}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-[box-shadow] duration-200 text-left cursor-pointer active:scale-[0.99]"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{"Total Patients"}</p>
                <p className="text-4xl font-bold text-teal-600 mt-2">
                  <AnimatedNumber value={patients.length} />
                </p>
              </div>
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
                <Users className="text-teal-600" size={28} />
              </div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab("doctors")}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-[box-shadow] duration-200 text-left cursor-pointer active:scale-[0.99]"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{"Total Doctors"}</p>
                <p className="text-4xl font-bold text-blue-600 mt-2">
                  <AnimatedNumber value={doctors.length} />
                </p>
              </div>
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Stethoscope className="text-blue-600" size={28} />
              </div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab("nurses")}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-[box-shadow] duration-200 text-left cursor-pointer active:scale-[0.99]"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{"Total Nurses"}</p>
                <p className="text-4xl font-bold text-pink-600 mt-2">
                  <AnimatedNumber value={nurses.length} />
                </p>
              </div>
              <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center">
                <Hospital className="text-pink-600" size={28} />
              </div>
            </div>
          </button>
        </div>

        <PillTabs
          value={activeTab}
          onChange={setActiveTab}
          options={adminTabOptions}
          layoutId="admin-main-tabs"
          fullWidth
          className="mb-6"
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {activeTab === "patients"
                ? "Patients Management"
                : activeTab === "doctors"
                  ? "Doctors Management"
                  : activeTab === "nurses"
                    ? "Nurses Management"
                    : activeTab === "deletions"
                      ? "Account Deletion Requests"
                      : activeTab === "processing-activities"
                        ? "Processing Activities"
                        : "Compliance Audit Log"}
            </h2>
            {activeTab !== "audit" && activeTab !== "deletions" && activeTab !== "processing-activities" && (
              <button
                onClick={() =>
                  openModal(
                    activeTab === "patients" ? "patient" : activeTab === "doctors" ? "doctor" : "nurse"
                  )
                }
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-sm"
              >
                <Plus size={18} />
                {activeTab === "patients" ? "Add New Patient" : activeTab === "doctors" ? "Add New Doctor" : "Add New Nurse"}
              </button>
            )}
          </div>

          {activeTab === "audit" && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {"Review security and compliance events recorded by the system."}
              </p>
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                  onClick={() => handleExportAudit("excel")}
                >
                  {"Export Excel"}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                  onClick={() => handleExportAudit("pdf")}
                >
                  {"Export PDF"}
                </button>
              </div>
              {auditLoading ? (
                <p className="text-gray-500">{"Loading..."}</p>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[480px] overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          {[
                            ["id", "ID"],
                            ["occurred_at", "Time"],
                            ["event_category", "Category"],
                            ["event_action", "Action"],
                            ["outcome", "Outcome", OUTCOME_FILTER_OPTIONS, auditOutcomeFilter, setAuditOutcomeFilter],
                            ["actor_username", "User"],
                            ["http_method", "Method", HTTP_METHOD_FILTER_OPTIONS, auditMethodFilter, setAuditMethodFilter],
                            ["request_path", "Path"],
                            ["status_code", "HTTP"],
                            ["ip_address", "IP"],
                            ["resource_type", "Resource Type"],
                          ].map((col) => {
                            const [key, label, filterOptions, filterValue, setFilterValue] = col;
                            return (
                              <th
                                key={key}
                                className="px-2 py-2 text-start font-semibold text-gray-700 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSort("audit", key)}
                              >
                                <span className="inline-flex items-center">
                                  {label}
                                  <SortIcon columnKey={key} sortConfig={auditSort} />
                                  {filterOptions && setFilterValue ? (
                                    <HeaderFilterSelect
                                      value={filterValue}
                                      onChange={(value) => {
                                        setAuditSkip(0);
                                        setFilterValue(value);
                                      }}
                                      options={filterOptions}
                                    />
                                  ) : null}
                                </span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {auditLogs.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 text-gray-700">{row.id}</td>
                            <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap">
                              {formatAuditDateTime(row.occurred_at)}
                            </td>
                            <td className="px-2 py-1.5 text-gray-700">{row.event_category}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.event_action}</td>
                            <td className="px-2 py-1.5">
                              <span
                                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  row.outcome === "success"
                                    ? "bg-green-100 text-green-800"
                                    : row.outcome === "denied"
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-red-100 text-red-800"
                                }`}
                              >
                                {row.outcome}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-gray-700 max-w-[140px] truncate" title={row.actor_username || ""}>
                              {row.actor_username || (row.actor_user_id != null ? `#${row.actor_user_id}` : "—")}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">{row.http_method || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-600 max-w-[200px] truncate" title={row.request_path || ""}>
                              {row.request_path || "—"}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">{row.status_code ?? "—"}</td>
                            <td className="px-2 py-1.5 text-gray-600 font-mono text-xs">{row.ip_address || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-600">{row.resource_type || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">
                      {`Showing ${auditLogs.length ? auditSkip + 1 : 0}–${auditSkip + auditLogs.length} of ${auditTotal}`}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={auditSkip <= 0 || auditLoading}
                        onClick={() => setAuditSkip((s) => Math.max(0, s - auditPageSize))}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
                      >
                        {"Previous"}
                      </button>
                      <button
                        type="button"
                        disabled={auditSkip + auditPageSize >= auditTotal || auditLoading}
                        onClick={() => setAuditSkip((s) => s + auditPageSize)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
                      >
                        {"Next"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "deletions" && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {"Review account deletion requests submitted by users."}
              </p>
              {deletionLoading ? (
                <p className="text-gray-500">{"Loading..."}</p>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[480px] overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          {[
                            ["id", "ID"],
                            ["user_id", "User ID"],
                            ["username", "Username"],
                            ["user_email", "Email"],
                            ["user_type", "User Type"],
                            ["requested_at", "Requested At"],
                            ["status", "Status", [["", "All"], ["pending", "Pending"], ["processed", "Processed"], ["rejected", "Rejected"]], deletionStatusFilter, setDeletionStatusFilter],
                            ["processed_at", "Processed At"],
                          ].map((col) => {
                            const [key, label, filterOptions, filterValue, setFilterValue] = col;
                            return (
                              <th
                                key={key}
                                className="px-2 py-2 text-start font-semibold text-gray-700 cursor-pointer select-none whitespace-nowrap"
                                onClick={() => handleSort("deletions", key)}
                              >
                                <span className="inline-flex items-center">
                                  {label}
                                  <SortIcon columnKey={key} sortConfig={deletionSort} />
                                  {filterOptions && setFilterValue ? (
                                    <HeaderFilterSelect
                                      value={filterValue}
                                      onChange={(value) => {
                                        setDeletionSkip(0);
                                        setFilterValue(value);
                                      }}
                                      options={filterOptions}
                                    />
                                  ) : null}
                                </span>
                              </th>
                            );
                          })}
                          <th className="px-2 py-2 text-start font-semibold text-gray-700">{"Admin Notes"}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {deletionRequests.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 text-gray-800">{row.id}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.user_id}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.username || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.user_email || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-700">{row.user_type || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap">
                              {formatAuditDateTime(row.requested_at)}
                            </td>
                            <td className="px-2 py-1.5">
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                {row.status}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap">
                              {formatAuditDateTime(row.processed_at)}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600 max-w-[220px] truncate" title={row.admin_notes || ""}>
                              {row.admin_notes || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">
                      {`Showing ${deletionRequests.length ? deletionSkip + 1 : 0}–${deletionSkip + deletionRequests.length} of ${deletionTotal}`}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={deletionSkip <= 0 || deletionLoading}
                        onClick={() => setDeletionSkip((s) => Math.max(0, s - deletionPageSize))}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
                      >
                        {"Previous"}
                      </button>
                      <button
                        type="button"
                        disabled={deletionSkip + deletionPageSize >= deletionTotal || deletionLoading}
                        onClick={() => setDeletionSkip((s) => s + deletionPageSize)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
                      >
                        {"Next"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "processing-activities" && (
            <ProcessingActivitiesPanel apiBaseUrl={API_BASE_URL} />
          )}

          
          {activeTab === "patients" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-700">{"S,No"}</th>
                    <SortableHeader label="Name" columnKey="name" sortConfig={patientSort} onSort={(key) => handleSort("patients", key)} />
                    <SortableHeader
                      label="Gender"
                      columnKey="gender"
                      sortConfig={patientSort}
                      onSort={(key) => handleSort("patients", key)}
                      filterValue={patientGenderFilter}
                      onFilterChange={setPatientGenderFilter}
                      filterOptions={GENDER_FILTER_OPTIONS}
                    />
                    <SortableHeader label="Assigned Clinicians" columnKey="clinician" sortConfig={patientSort} onSort={(key) => handleSort("patients", key)} />
                    <SortableHeader
                      label="Status"
                      columnKey="status"
                      sortConfig={patientSort}
                      onSort={(key) => handleSort("patients", key)}
                      filterValue={patientStatusFilter}
                      onFilterChange={setPatientStatusFilter}
                      filterOptions={STATUS_FILTER_OPTIONS}
                    />
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">{"Actions"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visiblePatients.map((patient, idx) => (
                    <tr key={patient.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                        {patient.firstName} {patient.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{patient.gender || "N/A"}</td>
                      <td className="px-4 py-3 text-sm">
                        {patient.doctors && patient.doctors.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {patient.doctors.map((clinician, idx) => {
                              const isDoctor = clinician.role?.toLowerCase() === "doctor";
                              const isNurse = clinician.role?.toLowerCase() === "nurse";
                              return (
                                <span 
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    isDoctor 
                                      ? "bg-blue-100 text-blue-700"
                                      : isNurse
                                      ? "bg-pink-100 text-pink-700"
                                      : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {isDoctor ? "Dr." : isNurse ? "Nurse" : ""} {clinician.firstName} {clinician.lastName}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">{"No clinicians assigned"}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${accountStatusClass(patient.status)}`}>
                          {patient.status || "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleActiveStatus("patient", patient)}
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              patient.status === "Not active"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            }`}
                            title={patient.status === "Not active" ? "Activate" : "Deactivate"}
                          >
                            {patient.status === "Not active" ? "Activate" : "Deactivate"}
                          </button>
                          <button
                            onClick={() => openDoctorAssignModal(patient)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={"Assign Doctors"}
                          >
                            <UserPlus size={18} />
                          </button>
                          <button
                            onClick={() => openNurseAssignModal(patient)}
                            className="text-pink-600 hover:text-pink-800 transition-colors"
                            title={"Assign Nurses"}
                          >
                            <UserPlus size={18} />
                          </button>
                          <button
                            onClick={() => openModal("patient", patient)}
                            className="text-teal-600 hover:text-teal-800 transition-colors"
                            title={"Edit"}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm("patient", patient)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title={"Delete"}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visiblePatients.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        {"No patients found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          
          {activeTab === "doctors" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-700">{"S,No"}</th>
                    <SortableHeader label="Name" columnKey="name" sortConfig={doctorSort} onSort={(key) => handleSort("doctors", key)} />
                    <SortableHeader
                      label="Gender"
                      columnKey="gender"
                      sortConfig={doctorSort}
                      onSort={(key) => handleSort("doctors", key)}
                      filterValue={doctorGenderFilter}
                      onFilterChange={setDoctorGenderFilter}
                      filterOptions={GENDER_FILTER_OPTIONS}
                    />
                    <SortableHeader
                      label="Assign Medications"
                      columnKey="canAssignMedications"
                      sortConfig={doctorSort}
                      onSort={(key) => handleSort("doctors", key)}
                      filterValue={doctorMedsFilter}
                      onFilterChange={setDoctorMedsFilter}
                      filterOptions={YES_NO_FILTER_OPTIONS}
                      className="px-4 py-3 text-center text-sm font-semibold text-gray-700"
                    />
                    <SortableHeader
                      label="Status"
                      columnKey="status"
                      sortConfig={doctorSort}
                      onSort={(key) => handleSort("doctors", key)}
                      filterValue={doctorStatusFilter}
                      onFilterChange={setDoctorStatusFilter}
                      filterOptions={STATUS_FILTER_OPTIONS}
                    />
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">{"Actions"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleDoctors.map((doctor, idx) => (
                    <tr key={doctor.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                        {doctor.firstName} {doctor.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{doctor.gender || "N/A"}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleMedicationToggle(doctor.id, doctor.canAssignMedications)}
                          className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
                            doctor.canAssignMedications === "Yes"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {doctor.canAssignMedications === "Yes" ? <Check size={14} /> : <X size={14} />}
                          {doctor.canAssignMedications === "Yes" ? "Yes" : "No"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${accountStatusClass(doctor.status)}`}>
                          {doctor.status || "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleActiveStatus("doctor", doctor)}
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              doctor.status === "Not active"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            }`}
                            title={doctor.status === "Not active" ? "Activate" : "Deactivate"}
                          >
                            {doctor.status === "Not active" ? "Activate" : "Deactivate"}
                          </button>
                          <button
                            onClick={() => openModal("doctor", doctor)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={"Edit"}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm("doctor", doctor)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title={"Delete"}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleDoctors.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        {"No doctors found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          
          {activeTab === "nurses" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-start text-sm font-semibold text-gray-700">{"S,No"}</th>
                    <SortableHeader label="Name" columnKey="name" sortConfig={nurseSort} onSort={(key) => handleSort("nurses", key)} />
                    <SortableHeader
                      label="Gender"
                      columnKey="gender"
                      sortConfig={nurseSort}
                      onSort={(key) => handleSort("nurses", key)}
                      filterValue={nurseGenderFilter}
                      onFilterChange={setNurseGenderFilter}
                      filterOptions={GENDER_FILTER_OPTIONS}
                    />
                    <SortableHeader
                      label="Assign Medications"
                      columnKey="canAssignMedications"
                      sortConfig={nurseSort}
                      onSort={(key) => handleSort("nurses", key)}
                      filterValue={nurseMedsFilter}
                      onFilterChange={setNurseMedsFilter}
                      filterOptions={YES_NO_FILTER_OPTIONS}
                      className="px-4 py-3 text-center text-sm font-semibold text-gray-700"
                    />
                    <SortableHeader
                      label="Status"
                      columnKey="status"
                      sortConfig={nurseSort}
                      onSort={(key) => handleSort("nurses", key)}
                      filterValue={nurseStatusFilter}
                      onFilterChange={setNurseStatusFilter}
                      filterOptions={STATUS_FILTER_OPTIONS}
                    />
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">{"Actions"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleNurses.map((nurse, idx) => (
                    <tr key={nurse.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                        {nurse.firstName} {nurse.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{nurse.gender || "N/A"}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleMedicationToggle(nurse.id, nurse.canAssignMedications)}
                          className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
                            nurse.canAssignMedications === "Yes"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {nurse.canAssignMedications === "Yes" ? <Check size={14} /> : <X size={14} />}
                          {nurse.canAssignMedications === "Yes" ? "Yes" : "No"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${accountStatusClass(nurse.status)}`}>
                          {nurse.status || "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleActiveStatus("nurse", nurse)}
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              nurse.status === "Not active"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                            }`}
                            title={nurse.status === "Not active" ? "Activate" : "Deactivate"}
                          >
                            {nurse.status === "Not active" ? "Activate" : "Deactivate"}
                          </button>
                          <button
                            onClick={() => openModal("nurse", nurse)}
                            className="text-pink-600 hover:text-pink-800 transition-colors"
                            title={"Edit"}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm("nurse", nurse)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title={"Delete"}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleNurses.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        {"No nurses found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </PageEnter>

      
      <Modal open={showModal} onClose={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
            <DialogHeader
              title={`${editingItem ? 'Edit' : 'Add New'} ${
                modalType === 'patient' ? 'Patient' :
                  modalType === 'doctor' ? 'Doctor' : 'Nurse'
              }`}
              subtitle={
                editingItem
                  ? 'Update account details for this user.'
                  : 'Enter demographics and login details for the new account.'
              }
              onClose={closeModal}
              tone="muted"
              sticky
            />

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <DialogBody className="p-6">
              <div className="grid grid-cols-2 gap-4">
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{"First Name"} <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{"Last Name"} <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{"Gender"} <span className="text-red-500">*</span></label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
                  >
                    <option value="">{"Select Gender"}</option>
                    <option value="Male">{"Male"}</option>
                    <option value="Female">{"Female"}</option>
                  </select>
                </div>

                 <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{"Date of Birth"} <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{"Email"} <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{"Username"}</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                
                {(modalType === "doctor" || modalType === "nurse") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{"Can Assign Medications"}</label>
                    <select
                      name="canAssignMedications"
                      value={formData.canAssignMedications}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
                    >
                      <option value="No">{"No"}</option>
                      <option value="Yes">{"Yes"}</option>
                    </select>
                  </div>
                )}
              </div>
              </DialogBody>

              <DialogFooter className="sm:justify-stretch">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  {"Cancel"}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
                >
                  {editingItem ? "Update" : "Create"}
                </button>
              </DialogFooter>
            </form>
          </div>
      </Modal>

      
      {deleteItem && (
      <Modal open={showDeleteConfirm} onClose={closeDeleteConfirm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 relative">
            <ModalCloseButton onClose={closeDeleteConfirm} className="absolute top-4 right-4" />
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                {"Delete"} {deleteItem.type === "patient" ? "Patient" : deleteItem.type === "doctor" ? "Doctor" : "Nurse"}
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete{' '}
                <strong>{deleteItem.item.firstName} {deleteItem.item.lastName}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeDeleteConfirm}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  {"Cancel"}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  {"Delete"}
                </button>
              </div>
            </div>
          </div>
      </Modal>
      )}

      
      {selectedPatientForDoctors && (
      <Modal open={showDoctorAssignModal} onClose={closeDoctorAssignModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
            <DialogHeader
              title="Assign Doctors"
              subtitle={`Patient: ${selectedPatientForDoctors.firstName} ${selectedPatientForDoctors.lastName}`}
              onClose={closeDoctorAssignModal}
              tone="muted"
              sticky
            />

            <DialogBody className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                {"Select doctors to assign to this patient. You can select multiple doctors."}
              </p>

              <div className="space-y-2">
                {doctors.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{"No doctors available"}</p>
                ) : (
                  doctors.map((doctor) => (
                    <label
                      key={doctor.id}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedDoctorsToAssign.includes(doctor.id)
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDoctorsToAssign.includes(doctor.id)}
                        onChange={() => toggleDoctorSelection(doctor.id)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="ms-3 flex-1">
                        <p className="font-medium text-gray-800">
                          {"Dr."} {doctor.firstName} {doctor.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {doctor.speciality || "General Practice"} • {doctor.gender || "N/A"}
                        </p>
                      </div>
                      {selectedDoctorsToAssign.includes(doctor.id) && (
                        <Check className="text-blue-600" size={20} />
                      )}
                    </label>
                  ))
                )}
              </div>
            </DialogBody>

            <DialogFooter className="sm:justify-stretch">
              <button
                type="button"
                onClick={closeDoctorAssignModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                {"Cancel"}
              </button>
              <button
                type="button"
                onClick={handleDoctorAssignment}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {`Save Assignment (${selectedDoctorsToAssign.length} selected)`}
              </button>
            </DialogFooter>
          </div>
      </Modal>
      )}

      
      {selectedPatientForNurses && (
      <Modal open={showNurseAssignModal} onClose={closeNurseAssignModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
            <DialogHeader
              title="Assign Nurses"
              subtitle={`Patient: ${selectedPatientForNurses.firstName} ${selectedPatientForNurses.lastName}`}
              onClose={closeNurseAssignModal}
              tone="muted"
              sticky
            />

            <DialogBody className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                {"Select nurses to assign to this patient. You can select multiple nurses."}
              </p>

              <div className="space-y-2">
                {nurses.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">{"No nurses available"}</p>
                ) : (
                  nurses.map((nurse) => (
                    <label
                      key={nurse.id}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedNursesToAssign.includes(nurse.id)
                          ? "border-pink-500 bg-pink-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedNursesToAssign.includes(nurse.id)}
                        onChange={() => toggleNurseSelection(nurse.id)}
                        className="w-5 h-5 text-pink-600 rounded focus:ring-2 focus:ring-pink-500"
                      />
                      <div className="ms-3 flex-1">
                        <p className="font-medium text-gray-800">
                          {nurse.firstName} {nurse.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {"Nurse"} • {nurse.gender || "N/A"}
                        </p>
                      </div>
                      {selectedNursesToAssign.includes(nurse.id) && (
                        <Check className="text-pink-600" size={20} />
                      )}
                    </label>
                  ))
                )}
              </div>
            </DialogBody>

            <DialogFooter className="sm:justify-stretch">
              <button
                type="button"
                onClick={closeNurseAssignModal}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                {"Cancel"}
              </button>
              <button
                type="button"
                onClick={handleNurseAssignment}
                className="flex-1 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium"
              >
                {`Save Assignment (${selectedNursesToAssign.length} selected)`}
              </button>
            </DialogFooter>
          </div>
      </Modal>
      )}
    </div>
  );
};

export default AdminPanel;
